import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';

import { SUPPORTED_LANGS, DEFAULT_LANG, LANG_STORAGE_KEY } from '@/i18n';

const META = {
  fr: { short: 'FR', long: 'Français', flag: '🇫🇷' },
  en: { short: 'EN', long: 'English', flag: '🌐' },
  es: { short: 'ES', long: 'Español', flag: '🇪🇸' },
};

function rebuildPath(pathname, nextLang) {
  const segments = pathname.split('/').filter(Boolean);
  if (SUPPORTED_LANGS.includes(segments[0])) segments.shift();
  const tail = segments.join('/');
  if (nextLang === DEFAULT_LANG) return tail ? `/${tail}` : '/';
  return tail ? `/${nextLang}/${tail}` : `/${nextLang}`;
}

/**
 * 3-language picker. Active = bordeaux background. Migrated teal → bordeaux.
 */
export default function LanguageSwitcher({ variant = 'light' }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const change = (next) => {
    setOpen(false);
    if (next === current) return;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    i18n.changeLanguage(next);
    navigate(rebuildPath(location.pathname, next));
  };

  const dark = variant === 'dark';
  const trigger = dark
    ? 'text-cream-50 hover:bg-navy-700'
    : 'text-navy-900 hover:bg-cream-100 dark:text-cream-50 dark:hover:bg-white/10';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${META[current].long}`}
        className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-btn px-3 py-2 text-sm font-medium transition-colors ${trigger}`}
      >
        <span aria-hidden="true" className="text-base leading-none">{META[current].flag}</span>
        <span className="font-semibold tracking-wide">{META[current].short}</span>
        <ChevronDown size={14} aria-hidden="true" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label="Language"
          className="absolute right-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-card border border-navy-900/10 bg-cream-50 py-1 shadow-card-hover"
        >
          {SUPPORTED_LANGS.map((code) => {
            const isActive = code === current;
            return (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => change(code)}
                  role="option"
                  aria-selected={isActive}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? 'bg-bordeaux-600 text-cream-50 font-medium'
                      : 'text-gray-700 hover:bg-cream-100'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="text-base leading-none">{META[code].flag}</span>
                    <span>{META[code].long}</span>
                  </span>
                  {isActive ? (
                    <Check size={14} aria-hidden="true" />
                  ) : (
                    <span className="text-xs text-gray-700">{META[code].short}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
